/**
 * TicketHistory — a ticket's billing edits, newest first (#50 PR C, plan §2c, DEC-356).
 *
 * Who, when (local, with the zone's name — DEC-004), the reason as typed, and each
 * field's before → after as the BILLING_TICKET_EDITED audit row recorded it.
 */

import React from 'react';
import { formatUsdFromCents } from '../../lib/money';
import { formatTimestampWithZone } from '../../lib/timestamp';
import type { TicketEditChange, TicketEditHistoryEntry } from './hooks';

const FIELD_LABELS: Readonly<Record<TicketEditChange['field'], string>> = {
  net_bbl: 'Net volume (bbl)',
  hours: 'Hours',
  service_charge: 'Extra',
  line_item: 'Product',
  // #52 — the label is "Disposal fee – <site>" from the line's notes when it has one.
  disposal_fee: 'Disposal fee',
};

/** One change in words, e.g. "Hours: 3.25 → 2.75" or "Removed TANK WASH (2)". */
export function describeChange(c: TicketEditChange): string {
  const name =
    c.field === 'net_bbl' || c.field === 'hours'
      ? FIELD_LABELS[c.field]
      : (c.label ?? FIELD_LABELS[c.field]);
  if (c.op === 'ADD') return `Added ${name} (${c.after?.quantity ?? '?'})`;
  if (c.op === 'REMOVE') return `Removed ${name} (${c.before?.quantity ?? '?'})`;
  const before = c.before?.quantity ?? c.reviewedNetBbl?.before ?? '—';
  const after = c.after?.quantity ?? c.reviewedNetBbl?.after ?? '—';
  return `${name}: ${before} → ${after}`;
}

export function TicketHistory({
  history,
  timeZone,
}: {
  history: readonly TicketEditHistoryEntry[];
  timeZone: string;
}): React.JSX.Element {
  if (history.length === 0) {
    return (
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
        Billing hasn’t edited this ticket.
      </p>
    );
  }
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }} aria-label="Edit history">
      {history.map((h, i) => (
        <li
          key={`${h.occurredAt ?? 'edit'}-${String(i)}`}
          style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border-secondary)' }}
        >
          <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            {h.actorName ?? 'Someone'} ·{' '}
            {h.occurredAt === null ? '—' : formatTimestampWithZone(h.occurredAt, timeZone)}
            {h.ticketTotalCents !== null && (
              <>
                {' '}
                · {formatUsdFromCents(h.ticketTotalCents.before)} →{' '}
                {formatUsdFromCents(h.ticketTotalCents.after)}
              </>
            )}
          </div>
          <ul style={{ margin: '4px 0', paddingLeft: '18px', fontSize: '14px' }}>
            {(h.changes ?? []).map((c, j) => (
              <li key={j}>{describeChange(c)}</li>
            ))}
          </ul>
          {h.reason !== null && (
            <p style={{ fontSize: '14px', whiteSpace: 'pre-wrap', margin: 0 }}>“{h.reason}”</p>
          )}
        </li>
      ))}
    </ol>
  );
}
